import type { ReactNode } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BIG_VALUE_SX, BigNumber } from '../../components/BigNumber';
import { formatVolume } from '../../utils/format';
import type { Change } from '../chart/changeOverRange';
import {
    FLOW_GROUPS,
    hasFlow,
    type FlowGroupId,
    type LatestFlow,
} from '../flow/groups';
import { formatNetCapital, formatWallets } from '../flow/summary';
import type { Outcome } from '../model/types';
import { LiveProbability } from '../outcomes/LiveProbability';
import { volumeOf } from '../outcomes/ranking';

interface OutcomeStripProps {
    outcome: Outcome;
    color: string | null;
    showBack: boolean;
    showFlow: boolean;
    /** The latest flow is not known yet: its figures are placeholders. */
    flowPending?: boolean;
    /** The prices are on their way: the change over the range is a placeholder. */
    changePending?: boolean;
    flow: ReadonlyMap<FlowGroupId, LatestFlow> | undefined;
    onBack: () => void;
    change: Change | null;
    rangeLabel: string;
}

const FIGURE_VALUE_SX = {
    fontSize: '13px',
    fontFamily: 'monospace',
    fontWeight: 600,
} as const;

function Figure({
    label,
    value,
    children,
}: {
    label: string;
    value?: string;
    children?: ReactNode;
}) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography
                sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                }}
            >
                {label}
            </Typography>
            {children ?? <Typography sx={FIGURE_VALUE_SX}>{value}</Typography>}
        </Box>
    );
}

export function OutcomeStrip({
    outcome,
    color,
    showBack,
    showFlow,
    flowPending = false,
    changePending = false,
    flow,
    onBack,
    change,
    rangeLabel,
}: OutcomeStripProps) {
    return (
        <Box
            data-testid="outcome-strip"
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 3,
                rowGap: 0.5,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
            }}
        >
            {showBack && (
                <Button
                    size="small"
                    startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                    onClick={onBack}
                    sx={{ fontSize: '12px', textTransform: 'none', ml: -0.5 }}
                >
                    All outcomes
                </Button>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                {color && (
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '2px',
                            bgcolor: color,
                            flexShrink: 0,
                        }}
                    />
                )}
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {outcome.label}
                </Typography>
            </Box>
            <BigNumber
                value={
                    <LiveProbability
                        outcome={outcome}
                        sx={{ ...BIG_VALUE_SX, borderRadius: '2px', px: '2px' }}
                    />
                }
                change={change}
                changePending={changePending}
                rangeLabel={rangeLabel}
                sx={{ flexBasis: { xs: '100%', md: 'auto' } }}
            />
            <Figure label="Volume" value={formatVolume(volumeOf(outcome))} />
            {flowPending &&
                FLOW_GROUPS.map(group => (
                    <Figure key={group.id} label={group.label}>
                        {/* a stand-in of a usual length keeps the strip's
                            wrapping (and height) for when the flow comes */}
                        <Skeleton
                            data-testid="strip-flow-placeholder"
                            sx={{ display: 'flex' }}
                        >
                            <Typography sx={FIGURE_VALUE_SX}>
                                $764k YES · 21 wallets
                            </Typography>
                        </Skeleton>
                    </Figure>
                ))}
            {!flowPending &&
                showFlow &&
                FLOW_GROUPS.map(group => {
                    const value = flow?.get(group.id);
                    return (
                        <Figure
                            key={group.id}
                            label={group.label}
                            value={
                                value && hasFlow(value)
                                    ? `${formatNetCapital(value)} · ${formatWallets(value.wallets)}`
                                    : 'none'
                            }
                        />
                    );
                })}
        </Box>
    );
}
